// Configuración de módulos (feature flags). Equivale a la tabla
// `module_settings`: por cada módulo/submódulo guarda activo, alcance y orden.
// El sidebar, el buscador del encabezado y las rutas de App.js leen estos flags.
//
// Persistencia: localStorage (el ERP aún no tiene backend en producción), con
// versión de esquema y migración al cargar, igual que el resto del estado.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { NAV_ITEMS } from '../data/navigation';

const STORAGE_KEY = 'kota-module-settings';
export const MODULE_SETTINGS_SCHEMA_VERSION = 1;

// Módulos que no se pueden apagar (sin ellos no se puede volver a encender nada).
export const LOCKED_MODULES = new Set(['home-module', 'dashboard', 'admin']);

// Valores por defecto distintos de "activo". Construcción queda activo por
// defecto, pero se apaga desde Administración → Configuración de módulos.
const DEFAULT_OVERRIDES = {};

const ROLES_ALLOWED_TO_MANAGE = new Set(['super_user', 'project_director']);

// Registro plano: módulo → submódulos, en el orden de navigation.js.
export const MODULE_REGISTRY = NAV_ITEMS.map((item, index) => ({
  key: item.id,
  label: item.label,
  order: (index + 1) * 10,
  children: (item.children || []).map((child, childIndex) => ({
    key: child.id,
    label: child.label,
    order: (childIndex + 1) * 10
  }))
}));

const buildDefaults = () => {
  const modules = {};
  MODULE_REGISTRY.forEach((module) => {
    modules[module.key] = { active: DEFAULT_OVERRIDES[module.key] ?? true, scope: 'global', order: module.order };
    module.children.forEach((child) => {
      modules[child.key] = { active: DEFAULT_OVERRIDES[child.key] ?? true, scope: 'global', order: child.order };
    });
  });
  return { schemaVersion: MODULE_SETTINGS_SCHEMA_VERSION, modules };
};

// Migración: completa claves nuevas (módulos agregados después) con sus valores
// por defecto y descarta claves que ya no existen.
export const migrateModuleSettings = (stored) => {
  const defaults = buildDefaults();
  if (!stored || typeof stored !== 'object' || typeof stored.modules !== 'object') return defaults;
  const modules = {};
  Object.entries(defaults.modules).forEach(([key, value]) => {
    const saved = stored.modules[key];
    modules[key] = saved && typeof saved === 'object'
      ? {
          active: LOCKED_MODULES.has(key) ? true : saved.active !== false,
          scope: saved.scope || 'global',
          order: Number.isFinite(saved.order) ? saved.order : value.order
        }
      : value;
  });
  return { schemaVersion: MODULE_SETTINGS_SCHEMA_VERSION, modules };
};

const loadSettings = () => {
  try {
    return migrateModuleSettings(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'));
  } catch {
    return buildDefaults();
  }
};

const parentOf = new Map();
MODULE_REGISTRY.forEach((module) => module.children.forEach((child) => parentOf.set(child.key, module.key)));

const ModuleSettingsContext = createContext(null);

export const ModuleSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(loadSettings);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // sin almacenamiento los flags valen sólo para esta sesión
    }
  }, [settings]);

  const isModuleActive = useCallback((key) => settings.modules[key]?.active !== false, [settings]);

  // Una página está habilitada si ella y su módulo padre están activos.
  // Páginas fuera del registro (perfil, subpáginas de Proyectos) heredan del
  // módulo por prefijo, o quedan habilitadas.
  const isPageEnabled = useCallback(
    (pageId) => {
      if (!pageId) return true;
      const parent = parentOf.get(pageId) || (settings.modules[`${pageId.split('-')[0]}-module`] ? `${pageId.split('-')[0]}-module` : null);
      if (parent && !isModuleActive(parent)) return false;
      if (settings.modules[pageId] && !isModuleActive(pageId)) return false;
      return true;
    },
    [settings, isModuleActive]
  );

  const setModuleActive = useCallback((key, active) => {
    if (LOCKED_MODULES.has(key)) return;
    setSettings((prev) => ({
      ...prev,
      modules: { ...prev.modules, [key]: { ...(prev.modules[key] || {}), active: Boolean(active) } }
    }));
  }, []);

  // Sube o baja un módulo de primer nivel en el sidebar.
  const moveModule = useCallback((key, direction) => {
    setSettings((prev) => {
      const ordered = MODULE_REGISTRY.map((module) => ({ key: module.key, order: prev.modules[module.key]?.order ?? module.order }))
        .sort((a, b) => a.order - b.order);
      const index = ordered.findIndex((entry) => entry.key === key);
      const target = index + (direction === 'up' ? -1 : 1);
      if (index < 0 || target < 0 || target >= ordered.length) return prev;
      [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
      const modules = { ...prev.modules };
      ordered.forEach((entry, position) => {
        modules[entry.key] = { ...modules[entry.key], order: (position + 1) * 10 };
      });
      return { ...prev, modules };
    });
  }, []);

  const resetModuleSettings = useCallback(() => setSettings(buildDefaults()), []);

  // NAV_ITEMS filtrado por flags y ordenado según la configuración.
  const navItems = useMemo(
    () =>
      NAV_ITEMS.filter((item) => isModuleActive(item.id))
        .map((item) => (item.children ? { ...item, children: item.children.filter((child) => isModuleActive(child.id)) } : item))
        .filter((item) => !item.children || item.children.length > 0)
        .sort((a, b) => (settings.modules[a.id]?.order ?? 0) - (settings.modules[b.id]?.order ?? 0)),
    [settings, isModuleActive]
  );

  const value = useMemo(
    () => ({
      settings,
      navItems,
      isModuleActive,
      isPageEnabled,
      setModuleActive,
      moveModule,
      resetModuleSettings,
      canManageModules: (user) => ROLES_ALLOWED_TO_MANAGE.has(user?.role)
    }),
    [settings, navItems, isModuleActive, isPageEnabled, setModuleActive, moveModule, resetModuleSettings]
  );

  return <ModuleSettingsContext.Provider value={value}>{children}</ModuleSettingsContext.Provider>;
};

export const useModuleSettings = () => {
  const context = useContext(ModuleSettingsContext);
  if (!context) throw new Error('useModuleSettings debe utilizarse dentro de ModuleSettingsProvider');
  return context;
};
